import { useMutation } from "@tanstack/react-query";

import { useUser } from "@/contexts/user-provider";

import { SelfInvitePolicy } from "@/types/enums/google-calendar-invite-policy";

import {
  updateAdjustCalendarHours,
  updateAppointmentTimeStep,
  updateAutoFilterNotesToSelf,
  updateAutoSignOffNotes,
  updateCalendarGridInterval,
  updateCalendarTextSize,
  updateCheckoutAppointmentSyncPreference,
  updateHideSupportChat,
  updateMarkChatReadOnOpen,
  updateOverviewLayout,
  updatePatientPortalConfig,
  updatePortalDashboardConfig,
  updateSendGcalInvitesSelf,
  updateShowDeletedProviders,
  updateShowGhostEvent,
  updateShowIds,
  updateShowMessagesBox,
  updateShowPatientIds,
  updateShowTranslucentPastEvents,
  updateShowWeekendsOnCalendar,
  updateTimezoneOverride,
} from "@/api/provider/admin/settings";

// Mutation hooks for preferences
export const useUpdateShowIdsMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateShowIds,
    onMutate: async (showIds) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({ ...currentUser, showIds });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateShowPatientIdsMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateShowPatientIds,
    onMutate: async (showPatientIds) => {
      if (!currentUser) return;
      const previousShowPatientIds = currentUser.showPatientIds;
      setCurrentUser((user) => (user ? { ...user, showPatientIds } : user));
      return { previousShowPatientIds };
    },
    // Roll back only this field, and only if no newer toggle has changed it since.
    onError: (_error, showPatientIds, context) => {
      if (!context) return;
      setCurrentUser((user) =>
        user && user.showPatientIds === showPatientIds
          ? { ...user, showPatientIds: context.previousShowPatientIds }
          : user,
      );
    },
  });
};

export const useUpdateShowWeekendsMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateShowWeekendsOnCalendar,
    onMutate: async (showWeekendsOnCalendar) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({ ...currentUser, showWeekendsOnCalendar });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateSendGcalInvitesSelfMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: (policy: SelfInvitePolicy | null) =>
      updateSendGcalInvitesSelf(policy),
    onMutate: async (policy) => {
      if (!currentUser) return;
      const previousPolicy = currentUser.sendGcalInvitesSelf;
      setCurrentUser((prev) =>
        prev ? { ...prev, sendGcalInvitesSelf: policy } : prev,
      );
      return { previousPolicy };
    },
    onError: (_error, policy, context) => {
      if (!context) return;
      setCurrentUser((prev) => {
        if (!prev || prev.sendGcalInvitesSelf !== policy) return prev;
        return {
          ...prev,
          sendGcalInvitesSelf: context.previousPolicy,
        };
      });
    },
  });
};

export const useUpdateShowTranslucentPastEventsMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateShowTranslucentPastEvents,
    onMutate: async (showTranslucentPastEvents) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({ ...currentUser, showTranslucentPastEvents });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateShowGhostEventMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateShowGhostEvent,
    onMutate: async (showGhostEvent) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({ ...currentUser, showGhostEvent });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateShowDeletedProvidersMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateShowDeletedProviders,
    onMutate: async (showDeletedProviders) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({ ...currentUser, showDeletedProviders });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateShowMessagesBoxMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateShowMessagesBox,
    onMutate: async (showMessagesBox) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({ ...currentUser, showMessagesBox });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateMarkChatReadOnOpenMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateMarkChatReadOnOpen,
    onMutate: async (markChatReadOnOpen) => {
      if (currentUser) {
        setCurrentUser({ ...currentUser, markChatReadOnOpen });
        return { previous: currentUser.markChatReadOnOpen };
      }
    },
    // Undo only this field, and only while it still holds this toggle's value,
    // so a failure never reverts another preference saved meanwhile.
    onError: (_error, markChatReadOnOpen, context) => {
      if (!context) return;
      setCurrentUser((user) =>
        user && user.markChatReadOnOpen === markChatReadOnOpen
          ? { ...user, markChatReadOnOpen: context.previous }
          : user,
      );
    },
  });
};

export const useUpdateAutoFilterNotesToSelfMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateAutoFilterNotesToSelf,
    onMutate: async (autoFilterNotesToSelf) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({ ...currentUser, autoFilterNotesToSelf });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateAutoSignOffNotesMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateAutoSignOffNotes,
    onMutate: async (autoSignOffNotes) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({ ...currentUser, autoSignOffNotes });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateAdjustCalendarHoursMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: ({
      startHour,
      endHour,
    }: {
      startHour: number;
      endHour: number;
    }) => updateAdjustCalendarHours(startHour, endHour),
    onMutate: async ({ startHour, endHour }) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({
          ...currentUser,
          startCalendarHour: startHour,
          endCalendarHour: endHour,
        });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateCalendarGridIntervalMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: (gridInterval: number) =>
      updateCalendarGridInterval(gridInterval),
    onMutate: async (gridInterval) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({
          ...currentUser,
          calendarGridInterval: gridInterval || null,
        });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateAppointmentTimeStepMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateAppointmentTimeStep,
    onMutate: async (stepMinutes) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({
          ...currentUser,
          appointmentTimeStepMinutes: stepMinutes,
        });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateCalendarTextSizeMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateCalendarTextSize,
    onMutate: async (calendarTextSize) => {
      if (!currentUser) return;
      const previousSize = currentUser.calendarTextSize ?? "small";
      setCurrentUser((previousUser) =>
        previousUser ? { ...previousUser, calendarTextSize } : previousUser,
      );
      return { previousSize };
    },
    onError: (_error, calendarTextSize, context) => {
      if (!context) return;
      setCurrentUser((previousUser) =>
        previousUser && previousUser.calendarTextSize === calendarTextSize
          ? { ...previousUser, calendarTextSize: context.previousSize }
          : previousUser,
      );
    },
  });
};

export const useUpdateCheckoutAppointmentSyncPreferenceMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateCheckoutAppointmentSyncPreference,
    onMutate: async (checkoutAppointmentSyncPreference) => {
      if (!currentUser) return;
      const previousPreference =
        currentUser.checkoutAppointmentSyncPreference ?? "ask";
      setCurrentUser((previousUser) =>
        previousUser
          ? { ...previousUser, checkoutAppointmentSyncPreference }
          : previousUser,
      );
      return { previousPreference };
    },
    onError: (_error, checkoutAppointmentSyncPreference, context) => {
      if (!context) return;
      setCurrentUser((previousUser) => {
        if (
          !previousUser ||
          previousUser.checkoutAppointmentSyncPreference !==
            checkoutAppointmentSyncPreference
        ) {
          return previousUser;
        }
        return {
          ...previousUser,
          checkoutAppointmentSyncPreference: context.previousPreference,
        };
      });
    },
  });
};

export const useUpdateHideSupportChatMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateHideSupportChat,
    onMutate: async (hideSupportChat) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({ ...currentUser, hideSupportChat });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdateTimezoneOverrideMutation = () => {
  const { currentUser, setCurrentUser } = useUser();

  return useMutation({
    mutationFn: updateTimezoneOverride,
    onMutate: async (timezoneOverride) => {
      if (currentUser) {
        const previousUser = { ...currentUser };
        setCurrentUser({ ...currentUser, timezoneOverride });
        return { previousUser };
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        setCurrentUser(context.previousUser);
      }
    },
  });
};

export const useUpdatePortalDashboardConfigMutation = () => {
  return useMutation({
    mutationFn: updatePortalDashboardConfig,
  });
};

export const useUpdatePatientPortalConfigMutation = () => {
  return useMutation({
    mutationFn: updatePatientPortalConfig,
  });
};

export const useUpdateOverviewLayoutMutation = () => {
  return useMutation({
    mutationFn: updateOverviewLayout,
  });
};
